import { isBxgyDiscount, isFixedAmountDiscount, isPercentageDiscount } from '@/models/Discount';
import { Amount } from '@/models/Price';
import { OrderItem } from '@/modules/order/models/OrderItem';
import { roundPayableAmount, roundUnitPrice } from '@/utils/currency/roundPrice';

export interface ConfirmedOrderItemPrice {
  lineItemPrice: Amount;
  discountAmount: Amount;
}

const isWithinQuantityRange = (quantity: number, quantityFrom?: number, quantityTo?: number) =>
  (quantityFrom === undefined || quantity >= quantityFrom) &&
  (quantityTo === undefined || quantity <= quantityTo);

export const calculateConfirmedOrderItemPrice = (
  item: OrderItem,
  quantity: number,
): ConfirmedOrderItemPrice | undefined => {
  if (!item.unitPrice) {
    return;
  }

  const { currencyCode } = item.unitPrice;
  const unitPrice = roundUnitPrice(item.unitPrice.amount);
  const originalLineAmount = roundPayableAmount(unitPrice * quantity);
  const originalQuantity = item.confirmedQuantity ?? item.quantity;

  // Preserve the amounts already calculated for the order when the quantity has not changed.
  // They may include promotion eligibility that is not fully represented on the order item.
  if (quantity === originalQuantity && item.lineItemPrice) {
    const lineItemPrice = item.lineItemPrice;
    const discountAmount = item.discountAmount ?? {
      currencyCode: lineItemPrice.currencyCode,
      amount: roundPayableAmount(originalLineAmount - lineItemPrice.amount),
    };

    return { lineItemPrice, discountAmount };
  }

  let confirmedLineAmount = originalLineAmount;

  for (const promotion of item.promotions ?? []) {
    const applicableDiscount = promotion.discounts.find((discount) => {
      if (isBxgyDiscount(discount)) {
        return quantity >= discount.buyQuantity + discount.getQuantity;
      }

      return isWithinQuantityRange(quantity, discount.quantityFrom, discount.quantityTo);
    });

    if (!applicableDiscount) {
      continue;
    }

    if (isPercentageDiscount(applicableDiscount)) {
      const discountedUnitPrice = roundUnitPrice(
        unitPrice * (1 - applicableDiscount.percentage / 100),
      );
      confirmedLineAmount = roundPayableAmount(discountedUnitPrice * quantity);
    } else if (isFixedAmountDiscount(applicableDiscount)) {
      const discountedUnitPrice = roundUnitPrice(unitPrice - applicableDiscount.amount.amount);
      confirmedLineAmount = roundPayableAmount(discountedUnitPrice * quantity);
    } else {
      const setSize = applicableDiscount.buyQuantity + applicableDiscount.getQuantity;
      const sets = Math.floor(quantity / setSize);
      const paidQuantity = sets * applicableDiscount.buyQuantity + (quantity % setSize);
      confirmedLineAmount = roundPayableAmount(paidQuantity * unitPrice);
    }

    break;
  }

  return {
    lineItemPrice: { currencyCode, amount: confirmedLineAmount },
    discountAmount: {
      currencyCode,
      amount: roundPayableAmount(originalLineAmount - confirmedLineAmount),
    },
  };
};
